import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { settingsGroups, allSections, type SettingsSection } from "@/pages/settings/settings-groups";
import { RoutePath } from "@/routes";
import { useGetAccount } from "@/resources/account/account.api";

export default function SettingsPage() {
  const [searchParams] = useSearchParams();
  const tab = searchParams.get("tab");
  const { data: account } = useGetAccount();

  // A section with a `visibleTo` is shown only once the account is loaded and says so. The same
  // filter drives the index and the `?tab=` lookup, so a hidden section is "not found" and its
  // component never mounts (no request fires). UX only: the endpoints enforce the boundary.
  const isVisible = (s: SettingsSection) => !s.visibleTo || (!!account && s.visibleTo(account));
  const active = tab ? allSections.filter(isVisible).find((s) => s.id === tab) : null;
  const visibleGroups = settingsGroups
    .map((g) => ({ ...g, sections: g.sections.filter(isVisible) }))
    .filter((g) => g.sections.length > 0);

  // AUT-399: each section already had a URL (`?tab=<id>`) but no link, so none of them could be
  // opened in a new tab. `sectionHref` hands that same URL to a real `Link`; other query params
  // are preserved exactly as `goTo` preserved them. Navigating by link also *pushes* history
  // rather than replacing it, so Back now returns to the index instead of leaving the page.
  const sectionHref = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) {
      next.set("tab", id);
    } else {
      next.delete("tab");
    }
    const query = next.toString();

    return query ? `${RoutePath.Settings}?${query}` : RoutePath.Settings;
  };

  if (active) {
    return (
      <div className="-ml-[29px] -mr-6 -mt-4 -mb-6 pl-[29px] pr-6 pt-4 pb-6 bg-card min-h-[calc(100vh-0px)]">
        <div className="space-y-6">
          <div className="space-y-2">
            <Link
              to={sectionHref(null)}
              className="flex items-center gap-1.5 text-sm font-semibold text-primary hover:text-primary/80 transition-colors w-fit"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to Global Settings
            </Link>
            <h1 className="text-3xl font-semibold text-foreground">{active.label}</h1>
            <p className="text-sm text-muted-foreground max-w-2xl">{active.description}</p>
          </div>
          {active.render()}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-5xl">
      <div className="space-y-1">
        <h1 className="text-3xl font-semibold text-foreground">Global Settings</h1>
        <p className="text-sm text-muted-foreground">
          Workspace-wide configuration for your team, brands, integrations, and AI.
        </p>
      </div>

      {visibleGroups.map((group) => (
        <section key={group.id} className="space-y-3">
          <div>
            <h3 className="text-xl font-semibold text-foreground">{group.label}</h3>
            <p className="text-sm text-muted-foreground mt-0.5">{group.description}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {group.sections.map((s) => {
              const Icon = s.icon;
              const card = (
                <Card
                  variant={s.disabled ? "default" : "interactive"}
                  className={cn(
                    "h-full p-5 group",
                    s.disabled && "opacity-40 cursor-not-allowed"
                  )}
                >
                  <div className="flex items-start gap-4">
                    <div className="h-10 w-10 rounded-md bg-primary-tint-soft text-primary flex items-center justify-center shrink-0">
                      <Icon className="h-5 w-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <h4 className="text-base font-semibold text-foreground">{s.label}</h4>
                          {s.disabled && (
                            <Badge variant="outline" className="text-2xs px-1.5 py-0">
                              Soon
                            </Badge>
                          )}
                        </div>
                        {!s.disabled && (
                          <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors shrink-0" />
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground mt-1">{s.description}</p>
                    </div>
                  </div>
                </Card>
              );

              // A disabled section has nowhere to go, so it stays a plain card. An enabled one is
              // wrapped rather than having `Card` grow a link mode -- nothing inside is
              // interactive, so an anchor around it is valid and needs no z-index juggling.
              return s.disabled ? (
                <div key={s.id}>{card}</div>
              ) : (
                <Link key={s.id} to={sectionHref(s.id)} className="block">
                  {card}
                </Link>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
